import React, { useContext, useEffect, useState } from "react";
import AppHeader from "./components/AppHeader";
import { AuthContext } from "./context/AuthContext";
import { Link } from "react-router-dom";
import api from "./api/api";

export default function Home() {
  const { isLoggedIn, user } = useContext(AuthContext);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [valuationPending, setValuationPending] = useState(false);
  const [lastValuationAt, setLastValuationAt] = useState(null);
  const [applying, setApplying] = useState(false);
  const [demoPanelVisible, setDemoPanelVisible] = useState(false);
  const [demoStats, setDemoStats] = useState({ count: 0, value: 0 });

  useEffect(() => {
    if (!isLoggedIn || user?.is_admin) return;
    api.get("/settings/")
      .then((res) => {
        setValuationPending(!!res.data.valuation_pending);
        setLastValuationAt(res.data.last_valuation_at || null);
      })
      .catch(() => {});
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (!user?.is_demo) return;
    if (sessionStorage.getItem("demo-panel-dismissed")) return;
    api.get("/cards/count").then(res => {
      setDemoStats({ count: res.data.count, value: res.data.total_value });
      setDemoPanelVisible(true);
    }).catch(() => {});
  }, [user?.is_demo]);

  const handleApplyNow = async () => {
    setApplying(true);
    try {
      await api.post("/cards/revalue-all");
      const sr = await api.get("/settings/");
      setValuationPending(false);
      setLastValuationAt(sr.data.last_valuation_at || null);
    } catch {
      alert("Failed to apply valuation. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const fmtDollar = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "$0";

  return (
    <>
      <AppHeader />

      {demoPanelVisible && (
        <div style={{
          margin: "1.25rem auto 0", maxWidth: "560px", padding: "1.25rem 1.5rem",
          border: "2px solid #f59e0b", borderRadius: "12px",
          background: "var(--bg-card)", position: "relative",
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
        }}>
          <div style={{ fontSize: "1.3rem", marginBottom: "0.4rem" }}>👋 Welcome to CardStoard Demo</div>
          <p style={{ margin: "0 0 0.75rem", color: "var(--text-muted)", fontSize: "1rem", lineHeight: 1.5 }}>
            You're browsing a demo card collection — <strong>{demoStats.count} cards</strong> valued at <strong>{fmtDollar(demoStats.value)}</strong>.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", margin: "0 0 1.25rem", fontSize: "1rem", color: "var(--text-secondary)" }}>
            <span>📇 <strong>Cards</strong> — browse, sort, and filter the full collection</span>
            <span>🎴 <strong>Gallery</strong> — view card photos front and back</span>
            <span>📊 <strong>Analytics</strong> — see value by player, brand, and era</span>
            <span>❓ <strong>Help</strong> — user guide and app information</span>
            <span>🚪 <strong>Exit Demo</strong> — return to the login screen</span>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
            <button className="nav-btn"
              style={{ fontSize: "0.9rem", padding: "0.4rem 1.25rem" }}
              onClick={() => { setDemoPanelVisible(false); sessionStorage.setItem("demo-panel-dismissed", "1"); sessionStorage.removeItem("demo-tour-done"); window.dispatchEvent(new CustomEvent("demo-start-tour")); }}>
              Take a Tour
            </button>
            <button onClick={() => { setDemoPanelVisible(false); sessionStorage.setItem("demo-panel-dismissed", "1"); }}
              className="nav-btn"
              style={{ fontSize: "0.9rem", padding: "0.4rem 1.25rem", background: "var(--bg-muted)", color: "var(--text-primary)" }}>
              Close
            </button>
          </div>
        </div>
      )}
      <div className="home-container" style={{ textAlign: "center", marginTop: "2rem" }}>
        
        {/* ✅ Logo and title */}
        <div className="logo-slot" style={{ marginBottom: "1rem" }}>
          <img
            src="/logo.png"
            alt="CardStoard Logo"
            style={{ width: "550px", height: "auto" }}
          />
        </div>

        <h2 style={{ color: "var(--accent-blue-dark)", fontWeight: "700", marginBottom: "0.5rem" }}>
          CardStoard
        </h2>

        <p
          className="home-subtitle"
          style={{ fontSize: "1.25rem", color: "#444", marginBottom: "1rem" }}
        >
          Collection Inventory and Valuation System
        </p>

        {/* ✅ Tagline */}
        <div
          style={{
            margin: "1rem auto 0.5rem",
            fontSize: "1.15rem",
            color: "#555",
          }}
        >
          Track cards 📇 | Manage inventory 📦 | Monitor value 📈
        </div>

        {/* ⭐ NEW — About link */}
        <div style={{ marginTop: "0.5rem", marginBottom: "2rem" }}>
          <Link
            to="/about"
            style={{
              color: "#167e30",
              fontWeight: "bold",
              textDecoration: "underline",
              fontSize: "1rem",
              cursor: "pointer",
            }}
          >
            About CardStoard
          </Link>
        </div>

        {/* Valuation update banner — non-admin users when admin has published new factors */}
        {isLoggedIn && !user?.is_admin && valuationPending && (
          <div style={{
            display: "inline-block",
            background: "var(--warn-bg)",
            border: "1px solid var(--warn-border)",
            borderRadius: "8px",
            padding: "0.75rem 1.25rem",
            marginTop: "1rem",
            marginBottom: "0.5rem",
            fontSize: "0.95rem",
            color: "var(--warn-text)",
            maxWidth: "480px",
          }}>
            <strong>⚡ Market factors updated</strong> — CardStoard has published new valuation factors.
            <div style={{ marginTop: "0.5rem" }}>
              <button
                className="nav-btn"
                onClick={handleApplyNow}
                disabled={applying}
                style={{ fontSize: "0.85rem", padding: "0.3rem 1rem" }}
              >
                {applying ? "Applying…" : "Apply to My Collection"}
              </button>
            </div>
          </div>
        )}

        {/* ✅ Quick user summary (if logged in) */}
        {isLoggedIn && (
          <div
            style={{
              display: "inline-block",
              background: "#f8f9fc",
              border: "1px solid #ddd",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
              borderRadius: "10px",
              padding: "1rem 2rem",
              marginTop: "1rem",
              fontSize: "1rem",
              color: "#333",
            }}
          >
            <p>
              Welcome back,{" "}
              <strong>{user?.username || user?.email?.split("@")[0]}</strong>!
            </p>
            <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "0.25rem" }}>
              Use the navigation bar above to view your cards, analytics, or settings.
            </p>
            {user?.subscription_tier && (
              <p style={{ fontSize: "0.8rem", color: "#666", marginTop: 0 }}>
                Plan:{" "}
                <span style={{
                  display: "inline-block", padding: "1px 10px", borderRadius: 12,
                  fontWeight: 700, fontSize: "0.78rem", color: "#fff",
                  background: { base: "#6c757d", rc: "#f97316", sp: "#28a745", elite: "#1976d2" }[user.subscription_tier] ?? "#6c757d"
                }}>{user.subscription_tier.toUpperCase()}</span>
              </p>
            )}
            {lastValuationAt && (
              <p style={{ fontSize: "0.8rem", color: "var(--text-faint)", marginTop: "0.25rem" }}>
                Last valuation: {new Date(lastValuationAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
